import { useCallback, useMemo, useState } from 'react';
import type { AdmissionField, AdmissionFormSection, BuiltInFormField } from '../../../../types/admissions';
import { isMissingOptions, isRequiredSystemFieldKey } from '../../form/admissionFormFields';

/** A field or section being edited, with an id of its own (new ones have no key until saved). */
export type DraftField = AdmissionField & { uid: string };
export type DraftSection = Omit<AdmissionFormSection, 'fields'> & { uid: string; fields: DraftField[] };

let next = 0;
const uid = () => `u${(next += 1)}`;

const toDraft = (sections: AdmissionFormSection[]): DraftSection[] =>
  [...sections].sort((a, b) => a.order - b.order).map(s => ({
    ...s,
    uid: uid(),
    fields: [...s.fields].sort((a, b) => a.order - b.order).map(f => ({ ...f, uid: uid() })),
  }));

/** What the server takes: order from position, no editor ids. New fields send no key. */
export const toSections = (draft: DraftSection[]): AdmissionFormSection[] =>
  draft.map(({ uid: _s, ...s }, order) => ({
    ...s,
    order,
    fields: s.fields.map(({ uid: _f, ...f }, i) => ({ ...f, order: i })),
  }));

const move = <T,>(list: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= list.length) return list;
  const out = [...list];
  const [item] = out.splice(from, 1);
  out.splice(to, 0, item);
  return out;
};

/**
 * The cycle's form while it's edited, and the rules for changing it:
 * required built-in fields stay on and required, a field applicants have
 * answered stays on the form with its type, and a section can go only when
 * nothing in it has to stay.
 */
export function useFormDraft(initial: AdmissionFormSection[], answeredKeys: ReadonlySet<string>) {
  const [sections, setSections] = useState<DraftSection[]>(() => toDraft(initial));
  const [dirty, setDirty] = useState(false);

  const change = useCallback((fn: (prev: DraftSection[]) => DraftSection[]) => {
    setSections(fn);
    setDirty(true);
  }, []);

  const mustStay = useCallback((f: AdmissionField) => isRequiredSystemFieldKey(f.key) || answeredKeys.has(f.key), [answeredKeys]);

  const editSection = useCallback((id: string, patch: Partial<DraftSection>) =>
    change(prev => prev.map(s => (s.uid === id ? { ...s, ...patch } : s))), [change]);

  const editField = useCallback((sectionId: string, fieldId: string, patch: Partial<AdmissionField>) =>
    change(prev => prev.map(s => (s.uid !== sectionId ? s : {
      ...s, fields: s.fields.map(f => (f.uid === fieldId ? { ...f, ...patch } : f)),
    }))), [change]);

  const ops = useMemo(() => ({
    addSection: (): string => {
      const s: DraftSection = { uid: uid(), key: '', title: 'New section', order: 0, fields: [] };
      change(prev => [...prev, s]);
      return s.uid;
    },
    removeSection: (id: string) => change(prev => prev.filter(s => s.uid !== id)),
    moveSection: (id: string, by: -1 | 1) => change(prev => {
      const i = prev.findIndex(s => s.uid === id);
      return move(prev, i, i + by);
    }),
    editSection,
    addQuestion: (sectionId: string): string => {
      const f: DraftField = {
        uid: uid(), key: '', label: '', type: 'text', required: false, enabled: true, isBuiltIn: false, isSystem: false, order: 0,
      };
      change(prev => prev.map(s => (s.uid === sectionId ? { ...s, fields: [...s.fields, f] } : s)));
      return f.uid;
    },
    addBuiltIn: (sectionId: string, field: AdmissionField) => change(prev => prev.map(s =>
      (s.uid === sectionId ? { ...s, fields: [...s.fields, { ...field, uid: uid() }] } : s))),
    removeField: (sectionId: string, fieldId: string) => change(prev => prev.map(s =>
      (s.uid === sectionId ? { ...s, fields: s.fields.filter(f => f.uid !== fieldId) } : s))),
    moveField: (sectionId: string, fieldId: string, by: -1 | 1) => change(prev => prev.map(s => {
      if (s.uid !== sectionId) return s;
      const i = s.fields.findIndex(f => f.uid === fieldId);
      return { ...s, fields: move(s.fields, i, i + by) };
    })),
    editField,
  }), [change, editSection, editField]);

  /** The first thing stopping a save, said plainly. */
  const problem = useMemo((): string | null => {
    for (const s of sections) {
      if (!s.title.trim()) return 'Every section needs a title.';
      for (const f of s.fields) {
        if (!f.label.trim()) return `A question in “${s.title}” has no wording yet.`;
        if (isMissingOptions(f)) return `“${f.label}” needs the choices families pick from.`;
      }
    }
    return null;
  }, [sections]);

  const stats = useMemo(() => {
    const fields = sections.flatMap(s => s.fields).filter(f => f.enabled !== false);
    return { sections: sections.length, questions: fields.length, required: fields.filter(f => f.required).length };
  }, [sections]);

  const usedKeys = useMemo(() => new Set(sections.flatMap(s => s.fields.map(f => f.key)).filter(Boolean)), [sections]);
  const missingBuiltIns = useCallback(
    (library: BuiltInFormField[]) => library.filter(b => !usedKeys.has(b.field.key)),
    [usedKeys],
  );

  return { sections, dirty, ops, problem, stats, mustStay, missingBuiltIns };
}

export type FormDraft = ReturnType<typeof useFormDraft>;
